import { ShieldOutlined, PeopleOutline, AddOutlined, Group, Person } from '@mui/icons-material';
import { CardContainer, Tag, DataTable, Button, SearchBar } from 'finara-components';
import type { DataTableColumn, TagVariant } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Navbar as AdminNavbar } from '@/components/admin/general/Navbar';
import { ErrorState, LoadingState } from '@/components/common';
import { AddUserModal } from '@/components/shared/AddUserModal';
import { ConfirmDeleteUserModal } from '@/components/shared/ConfirmDeleteUserModal';
import { EditUserModal } from '@/components/shared/EditUserModal';
import { UserActionsMenu } from '@/components/shared/UserActionsMenu';
import { Navbar as SuperAdminNavbar } from '@/components/super-admin/general/Navbar';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import { useCreateEmployee } from '@/hooks/queries/useEmployees';
import {
  useUsers,
  useUpdateUser,
  useToggleUserActive,
  useDeleteUser,
} from '@/hooks/queries/useUsers';
import { useAuth } from '@/hooks/useAuth';
import type { CreateEmployeeData } from '@/types/employees';
import type { User, UpdateUserData } from '@/types/user';

interface RoleCardData {
  id: string;
  name: string;
  displayName: string;
  description: string;
  activeUsers: number;
  permissions: number;
}

// Helper function to get the primary role from a user
const getUserRole = (user: User): string => {
  // If user has roles array, get the first role name
  if (user.roles && user.roles.length > 0) {
    return user.roles[0]?.name || 'unknown';
  }
  // Fallback to role field for backward compatibility
  return user.role || 'unknown';
};

export const RolesPage: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Determine which roles to filter based on user role
  const isSuperAdmin = user?.role === 'super_admin';
  const roleFilter = isSuperAdmin ? ['super_admin', 'admin', 'manager'] : ['admin', 'manager'];

  // Fetch users with role filter and organization filter
  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useUsers({
    roleNames: roleFilter,
    organizationId: isSuperAdmin ? undefined : user?.organization?.id, // Super admins see all, admins see only their org
    limit: 100,
    orderDirection: 'DESC',
  });

  // Mutations
  const createUser = useCreateEmployee();
  const updateUser = useUpdateUser();
  const toggleUserActive = useToggleUserActive();
  const deleteUserMutation = useDeleteUser();

  const allUsers = usersData?.data || [];

  // Filter users by search term
  const filteredUsers = allUsers.filter(userItem => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      userItem.name.toLowerCase().includes(searchLower) ||
      userItem.email.toLowerCase().includes(searchLower)
    );
  });

  // Handlers
  const handleAddUser = async (data: CreateEmployeeData) => {
    try {
      await createUser.mutateAsync(data);
      setIsAddModalOpen(false);
      toast.success('Usuario creado exitosamente');
    } catch (_error) {
      toast.error('Error al crear el usuario. Intenta nuevamente.');
    }
  };

  const handleEditUser = async (data: UpdateUserData) => {
    if (!selectedUser) return;

    try {
      await updateUser.mutateAsync({
        userId: selectedUser.id,
        data,
      });
      setIsEditModalOpen(false);
      setSelectedUser(null);
      toast.success('Usuario actualizado exitosamente');
    } catch (error) {
      if (error instanceof Error && error.message.includes('already exists')) {
        toast.error('Este correo electrónico ya está en uso. Usa otro correo.');
      } else {
        toast.error('Error al actualizar el usuario. Intenta nuevamente.');
      }
    }
  };

  const handleToggleActive = async (userToToggle: User) => {
    try {
      await toggleUserActive.mutateAsync({
        userId: userToToggle.id,
        data: { isActive: !userToToggle.isActive },
      });
      toast.success(`Usuario ${userToToggle.isActive ? 'desactivado' : 'activado'} exitosamente`);
    } catch (_error) {
      toast.error('Error al cambiar el estado del usuario. Intenta nuevamente.');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await deleteUserMutation.mutateAsync(selectedUser.id);
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      toast.success('Usuario eliminado exitosamente');
    } catch (_error) {
      toast.error('Error al eliminar el usuario. Intenta nuevamente.');
    }
  };

  const openEditModal = (userToEdit: User) => {
    setSelectedUser(userToEdit);
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (userToDelete: User) => {
    setSelectedUser(userToDelete);
    setIsDeleteModalOpen(true);
  };

  // Calculate role cards data
  const roleCards: RoleCardData[] = isSuperAdmin
    ? [
        {
          id: 'super_admin',
          name: 'super_admin',
          displayName: 'Super Admin',
          description: 'Acceso total al sistema y todas las organizaciones',
          activeUsers: allUsers.filter(u => getUserRole(u) === 'super_admin' && u.isActive).length,
          permissions: 15,
        },
        {
          id: 'admin',
          name: 'admin',
          displayName: 'Admin',
          description: 'Gestión completa de su organización',
          activeUsers: allUsers.filter(u => getUserRole(u) === 'admin' && u.isActive).length,
          permissions: 12,
        },
        {
          id: 'manager',
          name: 'manager',
          displayName: 'Manager',
          description: 'Gestión de empleados y operaciones',
          activeUsers: allUsers.filter(u => getUserRole(u) === 'manager' && u.isActive).length,
          permissions: 8,
        },
      ]
    : [
        {
          id: 'admin',
          name: 'admin',
          displayName: 'Admin',
          description: 'Gestión completa de su organización',
          activeUsers: allUsers.filter(u => getUserRole(u) === 'admin' && u.isActive).length,
          permissions: 12,
        },
        {
          id: 'manager',
          name: 'manager',
          displayName: 'Manager',
          description: 'Gestión de empleados y operaciones',
          activeUsers: allUsers.filter(u => getUserRole(u) === 'manager' && u.isActive).length,
          permissions: 8,
        },
      ];

  const getRoleVariant = (role: string): TagVariant => {
    switch (role) {
      case 'super_admin':
        return 'red';
      case 'admin':
        return 'blue';
      case 'manager':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getRoleDisplayName = (role: string): string => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'manager':
        return 'Manager';
      default:
        return role;
    }
  };

  const getStatusVariant = (isActive?: boolean): TagVariant => {
    return isActive ? 'green' : 'red';
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  const userColumns: DataTableColumn<User>[] = [
    {
      key: 'user',
      label: 'Usuario',
      render: (_, userItem) => (
        <div>
          <div className="font-medium text-brand-white">{userItem.name || 'Sin nombre'}</div>
          <div className="text-sm text-muted">{userItem.email}</div>
        </div>
      ),
    },
    // Only show organization column for super admins
    ...(isSuperAdmin
      ? [
          {
            key: 'organization' as const,
            label: 'Organización',
            render: (_: unknown, userItem: User) => (
              <div className="text-sm text-brand-white">{userItem.organization?.name || 'N/A'}</div>
            ),
          },
        ]
      : []),
    {
      key: 'role',
      label: 'Rol',
      render: (_, userItem) => (
        <Tag variant={getRoleVariant(getUserRole(userItem))}>
          {getRoleDisplayName(getUserRole(userItem))}
        </Tag>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, userItem) => (
        <Tag variant={getStatusVariant(userItem.isActive)}>
          {userItem.isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      key: 'registrationDate',
      label: 'Fecha de Registro',
      render: (_, userItem) => (
        <div className="text-sm text-brand-white">
          {formatDate(userItem.createdAt || userItem.created_at)}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, userItem) => (
        <UserActionsMenu
          onEdit={() => openEditModal(userItem)}
          onToggleActive={() => void handleToggleActive(userItem)}
          onDelete={() => openDeleteModal(userItem)}
          isActive={userItem.isActive}
        />
      ),
    },
  ];

  // Determine which navbar to use
  const navItems = isSuperAdmin ? superAdminNavItems : adminNavItems;
  const activeItem = isSuperAdmin ? 'Configuración & Seguridad' : 'Roles y Usuarios';

  return (
    <div className="min-h-screen">
      {isSuperAdmin ? (
        <SuperAdminNavbar
          navItems={navItems}
          userName={user.name || 'Usuario'}
          activeItem={activeItem}
        />
      ) : (
        <AdminNavbar
          navItems={navItems}
          userName={user?.name || 'Usuario'}
          activeItem={activeItem}
          pageTitle="Roles y Usuarios"
        />
      )}

      <div className="px-4 py-8">
        {/* Header */}
        <div className="flex flex-col lg:!flex-row items-start justify-between mb-6">
          <div className="mb-4 lg:!mb-0">
            <h1 className="text-2xl font-bold text-brand-white flex items-center gap-2">
              <ShieldOutlined />
              Roles y Permisos
            </h1>
            <p className="text-muted mt-1">
              Gestiona usuarios, roles y permisos del panel administrativo
            </p>
          </div>
          <Button
            variant="white"
            size="small"
            className="w-full lg:!w-auto"
            startIcon={<AddOutlined />}
            onClick={() => setIsAddModalOpen(true)}
          >
            Agregar Usuario
          </Button>
        </div>

        {isLoading ? (
          <LoadingState message="Cargando usuarios..." />
        ) : error ? (
          <ErrorState
            title="Error al cargar usuarios"
            message="No se pudieron cargar los usuarios. Por favor, intenta nuevamente."
            error={error}
            showRetry
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            {/* Roles Grid */}
            <div className="grid grid-cols-1 md:!grid-cols-3 gap-6 mb-6">
              {roleCards.map(role => (
                <CardContainer key={role.id}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-white/5 border border-border rounded-lg">
                      <Group className="text-muted" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-brand-white">{role.displayName}</h3>
                      <p className="text-sm text-muted mt-1">{role.description}</p>
                    </div>
                  </div>
                  <div className="flex justify-center items-center gap-4 text-sm text-muted">
                    <div className="flex items-center gap-1">
                      <Person fontSize="small" />
                      <span>{role.activeUsers} usuarios activos</span>
                    </div>
                  </div>
                </CardContainer>
              ))}
            </div>

            {/* Users Table */}
            <CardContainer>
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center gap-2">
                  <PeopleOutline className="text-muted" />
                  <h3 className="text-lg font-semibold text-brand-white">Usuarios del Sistema</h3>
                </div>
                <span className="text-sm text-muted">{allUsers.length} usuarios</span>
              </div>

              {/* Search Bar */}
              <div className="p-6 border-b border-border">
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Buscar por nombre o email..."
                  className="w-full"
                />
              </div>

              <div className="p-6">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <Person className="text-muted mx-auto mb-4" style={{ fontSize: '3rem' }} />
                    <h3 className="text-lg font-medium text-brand-white mb-2">
                      No se encontraron usuarios
                    </h3>
                    <p className="text-muted">
                      {searchTerm
                        ? 'Intenta con otros términos de búsqueda.'
                        : 'Agrega usuarios para comenzar.'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <DataTable columns={userColumns} data={filteredUsers} responsive />
                  </div>
                )}
              </div>
            </CardContainer>
          </>
        )}
      </div>

      {/* Modals */}
      <AddUserModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={data => void handleAddUser(data)}
        isLoading={createUser.isPending}
        isSuperAdmin={isSuperAdmin}
        organizationId={user?.organization?.id}
      />
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={data => void handleEditUser(data)}
        user={selectedUser}
        isLoading={updateUser.isPending}
      />

      <ConfirmDeleteUserModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={() => void handleDeleteUser()}
        userName={selectedUser?.name || selectedUser?.email || ''}
        isLoading={deleteUserMutation.isPending}
      />
    </div>
  );
};
